import { AtlassianNavigation, atlassianTheme, NavigationTheme } from '@atlaskit/atlassian-navigation';
import Page from '@atlaskit/page';
import ProgressBar from '@atlaskit/progress-bar';
import { colors } from '@atlaskit/theme';
import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import React, { PropsWithChildren } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';

import { ROUTE_HOME, ROUTE_NOTIFICATIONS } from '../../../constants/routes';
import FeatureFlagsStore from '../../../store/featureFlagsStore';
import Store from '../../../store/index';
import UIStore, { UILoadingState } from '../../../store/uiStore';
import UserStore from '../../../store/userStore';
import { fadeOut } from '../../styled/animations';

import NavAtlassianHome from './NavAtlassianHome';
import NavFullPageNotificationsButton from './NavFullPageNotificationsButton';
import NavHelpButton from './NavHelpButton';
import NavHomeButton from './NavHomeButton';
import NavNotificationsButton from './NavNotificationsButton';
import NavProfileButton from './NavProfileButton/index';
import NavQuickSearch, { NavQuickSearchSkeleton } from './NavQuickSearch';
import NavSettingsButton from './NavSettingsButton';
import NavSwitcherButton from './NavSwitcherButton';
import SkeletonHome from './SkeletonHome';
import SkeletonNavItem from './SkeletonNavItem';

type HomeLayoutV3Props = PropsWithChildren<{
  featureFlagsStore: FeatureFlagsStore;
  uiStore: UIStore;
  userStore: UserStore;
}> &
  RouteComponentProps;

const INITIAL_ANIMATION_DURATION_SECONDS = 7.5;
const IN_FLIGHT_PROGRESS_SIMULATION_VALUE = 0.95;

const ProgressBarContainer = styled.div<{ animationDurationSeconds: number }>`
  position: relative;
  z-index: 11;
  &.fade-out {
    animation: ${fadeOut} 1000ms ease forwards;
    animation-delay: 350ms;
  }
  [role='progressbar'] {
    position: absolute;
    height: 2.5px;
    margin-top: -1px;
    top: 0;
    left: 0;
    span {
      background-color: ${colors.B400};
      transition: width ${({ animationDurationSeconds }) => animationDurationSeconds}s
        cubic-bezier(0.02, 1.25, 0.48, 0.95);
    }
  }
`;

const navigationTheme: NavigationTheme = {
  ...atlassianTheme,
  mode: {
    ...atlassianTheme.mode,
    navigation: {
      ...atlassianTheme.mode.navigation,
      position: 'sticky',
      top: 0,
      zIndex: 12,
    },
  },
};

interface State {
  animationDurationSeconds: number;
  isLoading: boolean;
  progressValue: number;
}

const primaryItems = [<NavHomeButton />, <NavFullPageNotificationsButton />];

class HomeLayoutV3 extends React.Component<HomeLayoutV3Props, State> {
  state: State = {
    isLoading: true,
    progressValue: 0,
    animationDurationSeconds: INITIAL_ANIMATION_DURATION_SECONDS,
  };

  componentDidMount() {
    const { userStore, featureFlagsStore, uiStore } = this.props;
    when(() => userStore.isDone, this.recalculateProgress);
    when(() => featureFlagsStore.isDone, this.recalculateProgress);
    when(() => this.isLoadingDone(uiStore.loadingState.products), this.recalculateProgress);
    when(() => userStore.isDone && featureFlagsStore.isDone, () => this.setState({ isLoading: false }));
  }

  isLoadingDone = (state: UILoadingState) => [UILoadingState.ERROR, UILoadingState.READY_TO_RENDER].includes(state);

  recalculateProgress = () => {
    const { uiStore, location } = this.props;

    const homeLoadingIsDone = this.isLoadingDone(uiStore.loadingState.products);

    const loadingDone =
      (location.pathname === ROUTE_HOME && homeLoadingIsDone) || location.pathname === ROUTE_NOTIFICATIONS;

    const progressValue = loadingDone ? 1 : IN_FLIGHT_PROGRESS_SIMULATION_VALUE;
    const animationDurationSeconds = loadingDone ? 0.1 : INITIAL_ANIMATION_DURATION_SECONDS;
    this.setState({
      progressValue,
      animationDurationSeconds,
    });
  };

  render() {
    const { animationDurationSeconds, isLoading, progressValue } = this.state;

    return (
      <Page>
        <AtlassianNavigation
          label="Home"
          renderProductHome={isLoading ? SkeletonHome : NavAtlassianHome}
          renderAppSwitcher={isLoading ? SkeletonNavItem : NavSwitcherButton}
          renderProfile={isLoading ? SkeletonNavItem : NavProfileButton}
          renderSearch={isLoading ? NavQuickSearchSkeleton : NavQuickSearch}
          renderHelp={isLoading ? SkeletonNavItem : NavHelpButton}
          renderNotifications={isLoading ? SkeletonNavItem : NavNotificationsButton}
          renderSettings={isLoading ? SkeletonNavItem : NavSettingsButton}
          primaryItems={primaryItems}
          theme={navigationTheme}
        />
        <ProgressBarContainer
          animationDurationSeconds={animationDurationSeconds}
          className={`${progressValue >= 1 ? 'fade-out' : ''}`}
          aria-hidden={progressValue >= 1}
        >
          <ProgressBar value={progressValue} />
        </ProgressBarContainer>
        {this.props.children}
      </Page>
    );
  }
}

export default inject(({ userStore, featureFlagsStore, uiStore }: Store) => ({
  userStore,
  featureFlagsStore,
  uiStore,
  // @ts-ignore - withRouter prop typings are different to the other HOC default prop typings
}))(withRouter(observer(HomeLayoutV3)));
